/**
 * ExtraRates virtual views (B1): ten de-prefixed, ancestry-enriched surfaces
 * fronting the per-string `*_Agent` rate / injection /
 * composition / diagnostics sources.
 *
 * All ten sources are per-string grain (each carries exactly one `wellString`
 * direct relation; every measure column is a TimeSeries), so each is fronted by
 * ONE StringContext enriched view keeping identity + the wellString relation
 * (`STRING_KEEP`) and enriching every row with its well / field / reservoir
 * ancestry resolved through the FlatHierarchy registry (`STRING_CONTEXT_ENRICH`).
 * These are narrow single-concern surfaces — one view per source, no splitting.
 *
 * Each view de-prefixes its source's measure family to canonical names via the
 * shared shapes (the SSOT for column names / de-prefixing / descriptions).
 * Source typos and irregular casing are preserved VERBATIM in the shape
 * suffixes (see `shapes.ts`) so `prefix + suffix === source column`.
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { describeAncestry, describeStringScope, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  ALLOC_INJ_DAY_GAS,
  ALLOC_INJ_DAY_WATER,
  ALLOC_INJ_MONTH_GAS,
  ALLOC_INJ_MONTH_WATER,
  ALLOC_PROD_DAY,
  ALLOC_PROD_MONTH,
  CHOKE_MATCHED_RATES,
  FLUID_COMPOSITION,
  INJ_COMPLIANCE_GAS,
  INJ_COMPLIANCE_WATER,
  MEASURED_INJ_GAS,
  MEASURED_INJ_WATER,
  METHOD_ESTIMATE,
  METHOD_METHOD,
  MODEL_MATCH_ALARMS,
  MODEL_MATCHED_RATES,
  MODEL_RATIOS_LC,
  MODEL_RATIOS_SC,
  OPT_GAP_RATES,
  OPT_GAP_SETPOINTS,
  OPT_SRM_RATES,
  OPT_SRM_SCENARIO_META,
  PHASE_RATES_STD,
  RATE_DIAGNOSTICS,
  WELLTEST_CONTEXT,
  WELLTEST_META,
  WELLTEST_RATES_STD,
  WELLTEST_RATIOS,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import {
  AllocatedInjectionStringAgentView,
  AllocatedRatesDailyStringAgentView,
  DataDrivenRatesStringAgentView,
  MeasuredInjectionStringAgentView,
  MeasuredRatesWellTestStringAgentView,
  ModelMatchedRatesStringAgentView,
  OptimizedRatesSrmStringAgentView,
  PriorityRatesStringAgentView,
  RateDiagnosticsStringAgentView,
  StringFluidCompositionAgentView,
} from '#schema/generated/ipso/views.ts';

/** StringContext: well/field/reservoir ancestry off the `wellString` ref. */

/** One per-source ExtraRates surface: a canonical name, agent doc, source view, and the de-prefixed groups to keep. */
type ExtraRatesSpec = ViewFamilySpec & { readonly source: DataModelView };

/**
 * The ten ExtraRates surfaces, declared as data. Each fronts ONE per-string
 * `*_Agent` source, de-prefixing its measure family for the agent; all share
 * identity + the wellString relation and the well/field/reservoir ancestry
 * enrichment. The de-prefix axis differs per source (engine SRM/GAP, model/choke
 * match family, injection phase, allocation period, …) — see `shapes.ts` for the
 * verbatim suffix mapping and the preserved source typos.
 */
const EXTRA_RATES_SPECS: readonly ExtraRatesSpec[] = [
  {
    name: 'OptimizedRatesSrm',
    description: `Per-string SRM/GAP optimization results (WF17): scenario metadata, Sector-Reservoir-Model optimized production/injection rates, GAP network-optimized rates, GAP setpoints (choke/gas-lift/ESP/BHFP/WHP) and reservoir-condition rates${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: OptimizedRatesSrmStringAgentView,
    groups: [
      { shape: OPT_SRM_SCENARIO_META },
      { shape: OPT_SRM_RATES },
      { shape: OPT_GAP_RATES },
      { shape: OPT_GAP_SETPOINTS },
    ],
  },
  {
    name: 'ModelMatchedRates',
    description: `Per-string model-matched and choke-matched production rates plus model/historical/choke/PVT validation alarms and standard-conditions (SC) and line-conditions (LC) ratios${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: ModelMatchedRatesStringAgentView,
    groups: [
      { shape: MODEL_MATCH_ALARMS },
      { shape: MODEL_MATCHED_RATES, prefix: 'modelMatched' },
      { shape: CHOKE_MATCHED_RATES, prefix: 'chokeMatched' },
      { shape: MODEL_RATIOS_SC },
      { shape: MODEL_RATIOS_LC },
    ],
  },
  {
    name: 'MeasuredRatesWellTest',
    description: `Per-string well-test measured rates: standard-conditions oil/gas/water + liquid (Pts) rates, GOR/CGR/WGR/water-cut ratios (standard, in-situ and Pts variants), formation GOR, line-conditions CGR/WGR, and test metadata (start date, approved-allocation flag)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: MeasuredRatesWellTestStringAgentView,
    groups: [
      { shape: WELLTEST_RATES_STD, prefix: 'wellTest' },
      { shape: WELLTEST_RATIOS, prefix: 'wellTest' },
      { shape: WELLTEST_META, prefix: 'wellTest' },
      { shape: WELLTEST_CONTEXT },
    ],
  },
  {
    name: 'DataDrivenRates',
    description: `Per-string data-driven (ML) estimated production rates at standard conditions (oil/gas/water/liquid) plus gas-injection (gi) and water-injection (wi) compliance metrics (BHP, target-rate, technical-rate, WHP)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: DataDrivenRatesStringAgentView,
    groups: [
      { shape: PHASE_RATES_STD, prefix: 'dataDriven' },
      { shape: INJ_COMPLIANCE_GAS, prefix: 'gi' },
      { shape: INJ_COMPLIANCE_WATER, prefix: 'wi' },
    ],
  },
  {
    name: 'PriorityRates',
    description: `Per-string priority production rates at standard conditions (oil/gas/water/liquid), the flowing bottom-hole pressure, and the analysis method that produced them${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: PriorityRatesStringAgentView,
    groups: [
      { shape: METHOD_ESTIMATE, prefix: 'priority' },
      { shape: METHOD_METHOD, prefix: 'priority' },
    ],
  },
  {
    name: 'AllocatedRatesDaily',
    description: `Per-string allocated production volumes and uptime at daily (active hours) and monthly (active days) cadence for oil/gas/water (WF5)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: AllocatedRatesDailyStringAgentView,
    groups: [
      { shape: ALLOC_PROD_DAY, prefix: 'dayAllocProd' },
      { shape: ALLOC_PROD_MONTH, prefix: 'monthAllocProd' },
    ],
  },
  {
    name: 'AllocatedInjection',
    description: `Per-string allocated injection volumes and uptime for gas and water at daily (active hours) and monthly (active days) cadence (WF15)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: AllocatedInjectionStringAgentView,
    groups: [
      { shape: ALLOC_INJ_DAY_GAS, prefix: 'dayGasInj' },
      { shape: ALLOC_INJ_DAY_WATER, prefix: 'dayWaterInj' },
      { shape: ALLOC_INJ_MONTH_GAS, prefix: 'monthGasInj' },
      { shape: ALLOC_INJ_MONTH_WATER, prefix: 'monthWaterInj' },
    ],
  },
  {
    name: 'MeasuredInjection',
    description: `Per-string measured injection from wellhead meters (WF15): gas- and water-injection rate, wellhead pressure and choke opening${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: MeasuredInjectionStringAgentView,
    groups: [
      { shape: MEASURED_INJ_GAS, prefix: 'gasInj' },
      { shape: MEASURED_INJ_WATER, prefix: 'waterInj' },
    ],
  },
  {
    name: 'StringFluidComposition',
    description: `Per-string fluid composition: mol fractions (C1/C2/C3/C6/iC4/iC5/nC4/nC5/N2/CO2/H2S), condensate-gas and water-gas ratios, condensate gravity, fluid type, specific gas gravity and water salinity${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: StringFluidCompositionAgentView,
    groups: [{ shape: FLUID_COMPOSITION }],
  },
  {
    name: 'RateDiagnostics',
    description: `Per-string rate-estimation diagnostics (WF10): well performance score, liquid-rate change, gradient (GRD) analysis status, and the calculated/implied choke size and discharge coefficient${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: RateDiagnosticsStringAgentView,
    groups: [{ shape: RATE_DIAGNOSTICS }],
  },
];

/**
 * Build the ten de-prefixed, ancestry-enriched ExtraRates surfaces fronting the
 * agent-facing per-string tail, each a StringContext view over its own source +
 * the well/field/reservoir enrichment; filter by wellString (or join
 * WellEntity) to scope by hierarchy.
 */
export const useExtraRatesViews = makeStringContextViews({
  source: (spec: ExtraRatesSpec) => spec.source,
  specs: EXTRA_RATES_SPECS,
});
